import type { CardLoginEventDto, CustomerAccountDto } from '@gc/shared';

import { api, ApiError } from '@/lib/api';

/**
 * Pure endpoint and mapping helpers for the Cards screen (§5.7).
 *
 * Kept apart from the components so the load-bearing behaviour — the
 * unmatched-tap branch, the uppercased card id, the taken-card field error —
 * is unit-testable without a DOM.
 */

export const CARD_TAP_PATH = '/customers/card-tap';
export const CARD_LOGINS_PATH = '/customers/card-logins';

/**
 * Card ids are stored uppercased (the shared `cardId` zod schema transforms
 * them), so everything sent here is uppercased too — a lowercase UID typed by
 * hand must match the same card's tap.
 */
export function normalizeCardId(value: string): string {
  return value.trim().toUpperCase();
}

export type TapOutcome =
  { kind: 'matched'; account: CustomerAccountDto } | { kind: 'unmatched'; cardId: string };

/**
 * Resolve a tap against the venue's accounts.
 *
 * `card_not_recognised` is a RESULT, not a failure: the server has already
 * written the CardLoginEvent (a run of unmatched taps is the signal a reader
 * or a card is dying), and this screen's job is to show that outcome and
 * offer assignment. Anything else — network, permissions — still throws.
 */
export async function tapCard(rawCardId: string): Promise<TapOutcome> {
  const cardId = normalizeCardId(rawCardId);
  try {
    const account = await api.post<CustomerAccountDto>(CARD_TAP_PATH, { cardId });
    return { kind: 'matched', account };
  } catch (caught) {
    if (caught instanceof ApiError && caught.code === 'card_not_recognised') {
      return { kind: 'unmatched', cardId };
    }
    throw caught;
  }
}

/** Link a card to an account. Uniqueness is enforced server-side (`card_taken`). */
export function assignCard(customerId: string, rawCardId: string): Promise<CustomerAccountDto> {
  return api.patch<CustomerAccountDto>(`/customers/${customerId}`, {
    cardId: normalizeCardId(rawCardId),
  });
}

/** Explicit null clears the column; omitting the field would leave it untouched. */
export function unassignCard(customerId: string): Promise<CustomerAccountDto> {
  return api.patch<CustomerAccountDto>(`/customers/${customerId}`, { cardId: null });
}

/**
 * The message the server attached to one field, or null.
 *
 * The card-taken refusal arrives as `details: [{ field: 'cardId', … }]` from
 * the customers service, so it can be shown at the input that caused it
 * rather than as an anonymous banner.
 */
export function fieldErrorFor(caught: unknown, field: string): string | null {
  if (caught instanceof ApiError && Array.isArray(caught.details)) {
    const hit = (caught.details as { field?: string; message?: string }[]).find(
      (detail) => detail.field === field,
    );
    if (hit?.message) return hit.message;
  }
  return null;
}

/** An unrecognised tap handed to the assign panel for prefilling. */
export interface PendingAssignCard {
  cardId: string;
  /** Distinct per tap — see nextPendingAssign. */
  nonce: number;
}

/**
 * The next pending-card value for a tap.
 *
 * The nonce bumps on EVERY tap, including a repeat of the same card: the
 * assign panel keys its prefill effect on it, and a plain string would make a
 * repeat tap set identical state — React would skip the effect and staff who
 * had cleared the field would get nothing.
 */
export function nextPendingAssign(
  current: PendingAssignCard | null,
  cardId: string,
): PendingAssignCard {
  return { cardId, nonce: (current?.nonce ?? 0) + 1 };
}

export const LOGIN_FILTERS = ['all', 'matched', 'unmatched'] as const;
export type LoginFilter = (typeof LOGIN_FILTERS)[number];

/** The unmatched cut is the orphan-card list — cards nobody has assigned. */
export function filterLogins(
  events: readonly CardLoginEventDto[],
  filter: LoginFilter,
): CardLoginEventDto[] {
  if (filter === 'all') return [...events];
  const wantMatched = filter === 'matched';
  return events.filter((event) => event.succeeded === wantMatched);
}

/**
 * The customers list endpoint (§5.5) already searches name, phone and card,
 * which is exactly what the assign flow needs — no cards-specific search
 * exists server-side, deliberately.
 */
export function customerSearchPath(search: string): string {
  const params = new URLSearchParams();
  params.set('search', search.trim());
  params.set('sort', 'name');
  params.set('pageSize', '8');
  return `/customers?${params.toString()}`;
}
